import React, { useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { RowNode } from '@ag-grid-enterprise/all-modules';
import ConceptLinkerAiLinkerItemsModel from 'models/ConceptLinkerAiLinkerItemsModel';
import { ConceptLinkerAiLinkerModel } from 'models/ConceptLinkerAiLinkerModel';
import { useForceRender } from 'hooks/useForceRender';
import Grid from 'CMFW/grid/Grid';
import DeleteButton from 'CMFW/button/DeleteButton';
import ModuleContext from 'CMFW/context/ModuleContext';
import ValidatorHeader from 'components/ValidatorHeader';
import {
  ConceptLinkerAiLinkerItemStatus,
  IConceptLinkerAiLinkerItem,
  IConceptLinkerAiLinkerDialectResult,
} from 'types/conceptLinkerAiLinker';
import { asyncTaskStatusRenderer } from 'CMFW/grid/cellRenderers/asyncTaskStatus';
import { clearEngineRegistry } from 'CMFW/grid/cellRenderers/AiLinkerTextRenderer';
import { aiLinkerItemStatusRenderer } from 'CMFW/grid/cellRenderers/aiLinkerItemStatus';
import { IFields } from 'CMFW/grid/fields';
import ConceptLinkerAiLinkerSummary from './ConceptLinkerAiLinkerSummary';
import PrioritySelector from 'components/PrioritySelector';
import { Button } from '@material-ui/core';

function hasDialectDifferences(dialectResults: IConceptLinkerAiLinkerDialectResult[]): boolean {
  const dialectConceptMaps = dialectResults.map((dr) => {
    const map: Record<string, string> = {};
    (dr.phrases || []).forEach((phrase) => {
      (phrase.concepts || []).forEach((c) => {
        c.positions.forEach((pos) => {
          map[`${phrase.order}-${pos}`] = `${c.ref}:${c.refId}`;
        });
      });
    });
    return map;
  });

  const allKeys = new Set<string>();
  dialectConceptMaps.forEach((m) => Object.keys(m).forEach((k) => allKeys.add(k)));

  for (const key of allKeys) {
    const values = dialectConceptMaps.map((m) => m[key]);
    const unique = new Set(values.filter((v) => v !== undefined));
    if (unique.size > 1 || (unique.size === 1 && values.some((v) => v === undefined))) {
      return true;
    }
  }
  return false;
}

let currentFilter: { type: 'status'; value: string } | null = null;

interface IProps {
  courseId: number;
}

const ConceptLinkerAiLinkerDetails: React.FC<IProps> = ({ courseId }) => {
  const aiLinkerModel = useContext(ModuleContext) as ConceptLinkerAiLinkerModel;
  const jobId = aiLinkerModel.itemId;

  const forceRender = useForceRender();
  const [activeFilter, setActiveFilter] = useState<{ type: 'status'; value: string } | null>(null);
  const [jobPriority, setJobPriority] = useState<number | undefined>(undefined);
  currentFilter = activeFilter;

  const name = aiLinkerModel.getItemValue('name');
  const entityType = aiLinkerModel.getItemValue('entity_type');
  const lessonStart = aiLinkerModel.getItemValue('lesson_start');
  const lessonEnd = aiLinkerModel.getItemValue('lesson_end');
  const itemsModel = useMemo(
    () => new ConceptLinkerAiLinkerItemsModel({ render: forceRender }),
    [forceRender],
  );

  useEffect(() => {
    clearEngineRegistry();
    if (jobId > 0) {
      itemsModel.setJobId(jobId);
      setActiveFilter(null);
      aiLinkerModel.loadItemData(jobId).then((data) => {
        if (data && data.priority !== undefined) {
          setJobPriority(data.priority);
        }
      });
    }
  }, [jobId, itemsModel, aiLinkerModel]);

  useEffect(() => {
    itemsModel.loadingGrid = true;
    itemsModel.render();
    setTimeout(() => {
      itemsModel.loadingGrid = false;
      itemsModel.render();
    }, 0);
  }, [activeFilter, itemsModel]);

  const handleFilterChange = (type: 'status', value: string) => {
    if (activeFilter?.type === type && activeFilter?.value === value) {
      setActiveFilter(null);
    } else {
      setActiveFilter({ type, value });
    }
  };

  const clearFilter = () => {
    setActiveFilter(null);
  };

  const handleChangePriority = async (newPriority: number) => {
    await aiLinkerModel.changePriority(jobId, newPriority);
    setJobPriority(newPriority);
  };

  const handleBulkReview = async () => {
    await itemsModel.bulkReview();
    await aiLinkerModel.reload();
    forceRender();
  };

  const isFilterActive = () => currentFilter !== null;

  const isRowVisible = (node: RowNode) => {
    if (!currentFilter) return true;

    const data: IConceptLinkerAiLinkerItem = node.data;
    if (!data) return true;

    switch (currentFilter.value) {
      case 'pending':
        return data.status === ConceptLinkerAiLinkerItemStatus.PENDING;
      case 'needs_review':
        return data.status === ConceptLinkerAiLinkerItemStatus.NEEDS_REVIEW;
      case 'reviewed':
        return data.status === ConceptLinkerAiLinkerItemStatus.DONE;
      case 'error':
        return data.status === ConceptLinkerAiLinkerItemStatus.ERROR;
      case 'differences': {
        if (!data.ai_result?.dialects) return false;
        const dialectEntries = Object.values(data.ai_result.dialects);
        if (dialectEntries.length < 2) return false;
        return hasDialectDifferences(dialectEntries);
      }
    }

    return true;
  };

  const cellRenderers = useMemo(
    () => ({
      [IFields.aiLinkerText]: 'aiLinkerTextRenderer',
      [IFields.conceptValidatorActions]: 'aiLinkerActionRenderer',
      [IFields.conceptValidatorAiStatus]: asyncTaskStatusRenderer,
      [IFields.conceptValidatorItemStatus]: aiLinkerItemStatusRenderer,
      [IFields.moduleNavigation]: 'moduleNavigationRenderer',
      [IFields.conceptValidatorAiResult]: 'aiLinkerAiResultRenderer',
      [IFields.agent_prompt_log]: 'agentPromptLogRenderer',
    }),
    [],
  );

  const gridOptions = useMemo(
    () => ({
      getRowStyle: (params: any) => {
        if (params.data) {
          if (params.data.status === ConceptLinkerAiLinkerItemStatus.ERROR) {
            return { backgroundColor: '#ffebee' };
          }
          if (params.data.status === ConceptLinkerAiLinkerItemStatus.PENDING) {
            return { backgroundColor: '#fffef7' };
          }
        }
        return undefined;
      },
    }),
    [],
  );

  if (!jobId || jobId === -1) {
    return null;
  }

  const lessonRange = lessonEnd ? `L${lessonStart}-${lessonEnd}` : `L${lessonStart}+`;
  const reviewableCount =
    (itemsModel.gridData as IConceptLinkerAiLinkerItem[] | undefined)?.filter(
      (i) => i.status === ConceptLinkerAiLinkerItemStatus.NEEDS_REVIEW,
    ).length ?? 0;

  return (
    <Container>
      <ValidatorHeader
        id={jobId}
        name={name}
        idLabel="Job"
        customTitle={
          <TitleSection>
            <span>{name}</span>
            <Separator>-</Separator>
            <Detail>{entityType}</Detail>
            <Detail>{lessonRange}</Detail>
          </TitleSection>
        }
        bulkActions={
          <>
            <Button
              variant="outlined"
              size="small"
              onClick={handleBulkReview}
              disabled={reviewableCount === 0}
            >
              Mark All Reviewed ({reviewableCount})
            </Button>
            <PrioritySelector
              currentPriority={jobPriority ?? 1}
              onChangePriority={handleChangePriority}
              isProcessing={
                (itemsModel.gridData as IConceptLinkerAiLinkerItem[] | undefined)?.some(
                  (item) => item.status === ConceptLinkerAiLinkerItemStatus.PENDING,
                ) ?? false
              }
            />
          </>
        }
        itemsModel={itemsModel}
      />

      <ConceptLinkerAiLinkerSummary
        itemsModel={itemsModel}
        onFilterChange={handleFilterChange}
        activeFilter={activeFilter}
        onClearFilter={clearFilter}
      />

      <GridContainer>
        <ModuleContext.Provider value={itemsModel}>
          <Grid
            cellRenderers={cellRenderers}
            gridOptions={gridOptions}
            isFilterActive={isFilterActive}
            isRowVisible={isRowVisible}
          />
        </ModuleContext.Provider>
      </GridContainer>

      <DeleteButtonContainer>
        <ModuleContext.Provider value={aiLinkerModel}>
          <DeleteButton />
        </ModuleContext.Provider>
      </DeleteButtonContainer>
    </Container>
  );
};

export default ConceptLinkerAiLinkerDetails;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Separator = styled.span`
  color: #999;
  font-size: 20px;
`;

const Detail = styled.span`
  color: #666;
  font-size: 14px;
  background: #f0f0f0;
  padding: 2px 8px;
  border-radius: 4px;
`;

const GridContainer = styled.div`
  flex: 1;
  overflow: auto;
  position: relative;
`;

const DeleteButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  padding-top: 4px;
`;
